import LocalRobotDataState from 'models/robots/local-robot-data-state';
import OrganizationRole from '../../models/enums/organization-roles';
import SearchType from '../../models/enums/search';
import FormUser from '../../models/general/form-user';
import User, { UserData } from '../../models/general/user';
import PaginationWrapper from '../../models/misc/api/pagination-wrapper';
import Filters, { customCreateSearchParams } from '@common/enums/query-params/filters';
import { SearchResponse } from '../../models/misc/search-result';
import Mission from '../../models/missions/mission';
import MissionList from '../../models/missions/mission-list';
import { DeploymentDetails } from '../../models/organization/deployment';
import DeploymentWeeks from '../../models/organization/deployment-week';
import ExternalApi, { PostApiKey } from '../../models/organization/external-api';
import { OpenApiDocument } from '../../models/organization/openapi';
import FormOrganization from '../../models/organization/form-organization';
import ListOrganization, {
  OrganizationTreeNode,
} from '../../models/organization/list-organization';
import Organization from '../../models/organization/organization';
import OrganizationDeleteData from '../../models/organization/organization-delete-data';
import DailyCleanedArea from '../../models/robots/daily-cleaned-area';
import FormRobot from '../../models/robots/form-robot';
import Robot from '../../models/robots/robot';
import RobotAuthentication from '../../models/robots/robot-authentication';
import RobotContact from '../../models/robots/robot-contact';
import RobotInformation from '../../models/robots/robot-information';
import TorizonStatus from '../../models/robots/torizon-status';
import TorizonUpdateRequest from '../../models/robots/torizon-update-request';
import {
  currentVersionManagement,
  externalApiSchemaPath,
  managementApiInstance,
} from './axios-base';
import RobotTag from 'models/robots/robot-tag';
import { DeploymentBlock } from 'utils/deployment-timeline/layout';
import {
  MissionOutsideRange,
  MissionReassignment,
} from '../../models/missions/mission-outside-range';

class OrganizationDataService {
  async getOrganizationById(id: string): Promise<Organization> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/organizations/${id}/`,
    );
    return response.data;
  }

  async getAllOrganizationsById(filters: Filters): Promise<ListOrganization[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/organizations/?${customCreateSearchParams({
        ...filters,
      })}`,
    );
    return response.data;
  }

  async getOrganizationTree(id: string): Promise<OrganizationTreeNode[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/organizations/${id}/org-tree-list/`,
    );
    return response.data;
  }

  async getLocationSetById(id: string): Promise<ListOrganization[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/organizations/${id}/location-set/`,
    );
    return response.data;
  }

  async getOrganizationDeleteDataById(id: string): Promise<OrganizationDeleteData> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/organizations/${id}/delete/`,
    );
    return response.data;
  }

  async createSubOrganization(data: Partial<FormOrganization>): Promise<ListOrganization> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/organizations/`,
      data,
    );
    return response.data;
  }

  async createUser(data: Partial<FormUser>): Promise<User> {
    const response = await managementApiInstance.post(`/${currentVersionManagement}/users/`, data);
    return response.data;
  }

  async getOrganizationUsersById(filters: Filters): Promise<User[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/users/?${customCreateSearchParams(filters)}`,
    );
    return response.data;
  }

  async getUserById(id: string): Promise<User> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/users/${id}/`);
    return response.data;
  }

  async getAllUserDataById(id: string): Promise<UserData> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/users/${id}/user-data/`,
    );
    return response.data;
  }

  async createRobot(data: Partial<FormRobot>): Promise<Robot> {
    const response = await managementApiInstance.post(`/${currentVersionManagement}/robots/`, data);
    return response.data;
  }

  async patchUserById(id: string, data: Partial<FormUser>): Promise<User> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/users/${id}/`,
      data,
    );
    return response.data;
  }

  async deleteUserById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/users/${id}/`,
    );
    return response.data;
  }

  async patchRobotById(id: string, data: Partial<FormRobot>): Promise<RobotInformation> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${id}/`,
      data,
    );
    return response.data;
  }

  async patchRobotAuthenticationSettingsById(
    id: string,
    data: Partial<RobotAuthentication>,
  ): Promise<RobotAuthentication> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${id}/authentication-settings/`,
      data,
    );
    return response.data;
  }

  async getRobotById(id: string): Promise<RobotInformation> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/robots/${id}/`);
    return response.data;
  }

  async deleteRobotById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/robots/${id}/`,
    );
    return response.data;
  }

  async getDailyCleanedAreaById(id: string, year: string): Promise<DailyCleanedArea> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/daily-cleaned-area/?${customCreateSearchParams({
        year: year,
      } as Filters)}`,
    );
    return response.data;
  }

  async getOrganizationRobotsById(filters: Filters): Promise<PaginationWrapper<Robot>> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/?${customCreateSearchParams(filters)}`,
    );
    return response.data;
  }

  async getWireguardConfigById(id: string): Promise<string> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/wireguard-config`,
    );
    return response.data;
  }

  async getTorizonStatusById(id: string): Promise<TorizonStatus> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/torizon/status/`,
    );
    return response.data;
  }

  async postTorizonUpdateById(id: string): Promise<TorizonUpdateRequest> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${id}/torizon/update/`,
    );
    return response.data;
  }

  async postTorizonUpdateCancelById(id: string): Promise<void> {
    await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${id}/torizon/update/cancel/`,
    );
  }

  async getDeploymentsByRobotId(id: string): Promise<DeploymentDetails[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/deployments`,
    );
    return response.data;
  }

  async patchDeploymentByRobotId(
    robotId: string,
    data: Partial<DeploymentDetails>,
  ): Promise<DeploymentDetails> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/deployments/`,
      data,
    );
    return response.data;
  }

  async patchDeploymentDatesByRobotId(
    robotId: string,
    updates: DeploymentBlock[],
  ): Promise<DeploymentDetails[]> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/deployments/bulk-dates/`,
      updates,
    );
    return response.data;
  }

  async getMissionsOutsideRangeByDeployment(
    robotId: string,
    deploymentId: string,
  ): Promise<MissionOutsideRange[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${robotId}/missions-outside-range/`,
      { params: { deploymentId } },
    );
    return response.data;
  }

  async reassignMissionsByRobotId(
    robotId: string,
    reassignments: MissionReassignment[],
  ): Promise<DeploymentDetails[]> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/robots/${robotId}/missions/bulk-reassign/`,
      reassignments,
    );
    return response.data;
  }

  async getContactsByRobotId(id: string): Promise<RobotContact[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/contacts/`,
    );
    return response.data;
  }

  async getLocalRobotDataStateById(id: string): Promise<LocalRobotDataState> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/robots/${id}/local-data-state/`,
    );
    return response.data;
  }

  async getMissions(filters: Filters): Promise<PaginationWrapper<MissionList>> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/missions/?${customCreateSearchParams(filters)}`,
    );
    return response.data;
  }

  async getMissionById(id: string): Promise<Mission> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/missions/${id}/`,
    );
    return response.data;
  }

  async patchOrganizationById(id: string, data: Partial<FormOrganization>): Promise<Organization> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/organizations/${id}/`,
      data,
    );
    return response.data;
  }

  async deleteOrganizationById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/organizations/${id}/`,
    );
    return response.data;
  }

  async getDeploymentWeeksById(id: string, filters: Filters): Promise<DeploymentWeeks[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/organizations/${id}/deployment-weeks/?${customCreateSearchParams(filters)}`,
    );
    return response.data;
  }

  async getSearch(params: {
    search: string;
    fields: SearchType[];
    childOrgId?: string;
    orgRole?: OrganizationRole;
  }): Promise<SearchResponse> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/search/?${customCreateSearchParams(params)}`,
    );
    return response.data;
  }

  async getApiKey(organizationId: string): Promise<ExternalApi[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/api-key?${customCreateSearchParams({
        org: organizationId,
      } as Filters)}`,
    );
    return response.data;
  }

  async createApiKey(payload: PostApiKey): Promise<ExternalApi> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/api-key/`,
      payload,
    );
    return response.data;
  }

  async deleteApiKeyById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/api-key/${id}/`,
    );
    return response.data;
  }

  async getExternalApiSchema(): Promise<OpenApiDocument> {
    const response = await managementApiInstance.get(externalApiSchemaPath);
    return response.data;
  }

  async getTags(): Promise<string[]> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/tags/`);
    return response.data;
  }

  async postTag(tag: Partial<RobotTag>): Promise<RobotTag> {
    const response = await managementApiInstance.post(`/${currentVersionManagement}/tags/`, tag);
    return response.data;
  }

  async patchTagById(id: string, tag: Partial<RobotTag>): Promise<RobotTag> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/tags/${id}/`,
      tag,
    );
    return response.data;
  }

  async deleteTagById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(`/${currentVersionManagement}/tags/${id}/`);
    return response.data;
  }
}

export default new OrganizationDataService();
